import {
  AgGrid,
  Button,
  ErrorMessage,
  Heading,
  IconDropdown,
  InfoPopup,
  MobileResponsiveGrid,
  Spinner,
} from "@/common/components";
import { useContext, useState, useEffect, useSelector } from "@/common/hooks";
import { PrivateContext } from "@/routes/PrivateRoute";
import {
  useDeleteProductManufacturerContactForAdminMutation,
  useGetProductManufacturerContactQuery,
  useGetProductManufacturerForAdminQuery,
} from "@/service/AdminService";
import { setProductManufacturersData } from "@/store/adminSlice";
import { formatPhoneNumber, formatRoleCode } from "@/utils/Utilities";

const ProductManufacturers = () => {
  const {
    setNavHeader,
    setPageHeader,
    skipToken,
    Routes,
    navigate,
    labels,
    dispatch,
    setNavCancelFunc,
  } = useContext(PrivateContext);

  const [rowDataForPM, setRowDataForPM] = useState([]);
  const [productSponsorId, setProductSponsorId] = useState("");
  const [contactsParam, setContactsParam] = useState(skipToken);
  const [rowDataContacts, setRowDataContacts] = useState([]);
  const [showConfirmPopup, setShowConfirmPopup] = useState("");
  const [sortOptionPM, setSortOptionPM] = useState("Company Name (A to Z)");

  const storeData = useSelector(
    (state) => state?.adminReducer?.productManufacturers
  );

  const actionsDropdown = (props) => {
    return <IconDropdown {...props} list={props.list} />;
  };

  const columnDefsForPM = [
    {
      headerName: labels?.productManufacturer?.companyNameHeader,
      field: "name",
      flex: 2,
      sortable: true,
    },
    {
      headerName: labels?.productManufacturer?.actions,
      colId: "actions",
      cellRenderer: actionsDropdown,
      cellRendererParams: {
        list: [
          {
            text: labels?.productManufacturer?.viewManufacturerDetails,
            path: Routes.productManufacturerDetails.path,
          },
          {
            text: labels?.productManufacturer?.investmentVehicles,
            path: Routes.productManufacturerInvestmentVehicles.path,
          },
          {
            text: labels?.productManufacturer?.insuranceCompanies,
            path: Routes.productManufacturerInsuranceCompanies.path,
          },
        ],
      },
      sortable: false,
    },
    {
      headerName: labels?.productManufacturer?.externalIdHeader,
      field: "externalId",
      flex: 1.5,
    },
    {
      headerName: labels?.productManufacturer?.investmentVehicles,
      field: "numberOfInvestmentVehicles",
      cellStyle: { textAlign: "center" },
      headerClass: "text-center",
    },
    {
      headerName: labels?.productManufacturer?.insuranceCompanies,
      field: "numberOfInsuranceCompanies",
      cellStyle: { textAlign: "center" },
      headerClass: "text-center",
    },
  ];

  const columnDefsContacts = [
    {
      headerName: labels?.productManufacturer?.role,
      field: "roleCode",
      cellRenderer: formatRoleCode,
    },
    {
      headerName: labels?.productManufacturer?.contactFirstName,
      field: "firstName",
    },
    {
      headerName: labels?.productManufacturer?.actions,
      colId: "actions",
      cellRenderer: actionsDropdown,
      cellRendererParams: {
        list: [
          {
            text: labels?.productManufacturer?.details,
            click: (e) => getDetailsForContact(e),
          },
          {
            text: labels?.productManufacturer?.removeContact,
            click: (e) => setShowConfirmPopup(e.id),
          },
        ],
      },
      sortable: false,
    },
    {
      headerName: labels?.productManufacturer?.contactLastName,
      field: "lastName",
    },
    {
      headerName: labels?.productManufacturer?.contactPhone,
      field: "phoneNumber",
      cellRenderer: formatPhoneNumber,
    },
    {
      headerName: labels?.productManufacturer?.contactEmail,
      field: "emailAddress",
      flex: 1.5,
    },
  ];

  const {
    data: productManufacturers,
    isFetching,
    error,
  } = useGetProductManufacturerForAdminQuery();

  const {
    data: contacts,
    isFetching: isFetching1,
    error: error1,
  } = useGetProductManufacturerContactQuery(contactsParam);

  const [deleteContactApi, { isLoading: isFetching2, error: error2 }] =
    useDeleteProductManufacturerContactForAdminMutation();

  const clearStateInStore = () => {
    dispatch(setProductManufacturersData({}));
    navigate(-1);
    return;
  };

  const onCellClicked = (e) => {
    if (e.colDef.colId !== "actions") {
      setProductSponsorId(e.data.id);
    }
  };

  const getRowClassPM = (params) => {
    if (params.data.id === productSponsorId) {
      return "hover-bg";
    }
    return "";
  };

  const onFirstDataRendered = (params) => {
    const findIdx = rowDataForPM.findIndex((e) => e.id === productSponsorId);
    params.api.ensureIndexVisible(findIdx, "top");
  };

  const addNewManufacturer = () => {
    navigate(Routes.productManufacturerDetails.path);
  };

  const getDetailsForContact = (params) => {
    const data = rowDataContacts.find((e) => e.id === params.id);
    navigate(Routes.productManufacturerContactDetails.path, {
      state: {
        ...data,
        primaryExists:
          rowDataContacts.find((e) => e.roleCode === "P") &&
          data.roleCode !== "P",
      },
    });
  };

  const addNewContact = () => {
    navigate(Routes.productManufacturerContactDetails.path, {
      state: {
        productSponsorId: productSponsorId,
        primaryExists: rowDataContacts.find((e) => e.roleCode === "P"),
      },
    });
  };

  const removeContact = () => {
    deleteContactApi({ contactId: showConfirmPopup, productSponsorId })
      .unwrap()
      .then(() => {
        //
      });
  };

  const sortedEntitiesPM = [...rowDataForPM]?.sort((a, b) => {
    if (sortOptionPM === "Company Name (A to Z)") {
      return a?.name.localeCompare(b?.name);
    } else if (sortOptionPM === "Company Name (Z to A)") {
      return b?.name.localeCompare(a?.name);
    }
    return 0;
  });

  useEffect(() => {
    if (productManufacturers && productManufacturers?.productSponsors) {
      setRowDataForPM(productManufacturers?.productSponsors);
      if (!storeData?.productSponsorId) {
        setProductSponsorId(productManufacturers?.productSponsors[0].id);
      }
    }
  }, [productManufacturers]);

  useEffect(() => {
    if (contacts && contacts?.contacts) {
      setRowDataContacts(contacts?.contacts);
    }
  }, [contacts]);

  useEffect(() => {
    if (productSponsorId) {
      dispatch(
        setProductManufacturersData({ productSponsorId: productSponsorId })
      );
      setContactsParam({ productSponsorId: productSponsorId });
    }
  }, [productSponsorId]);

  useEffect(() => {
    setPageHeader(labels?.productManufacturer?.title);
    setNavHeader(labels?.navbar?.admin);
    setNavCancelFunc(() => clearStateInStore);

    if (storeData?.productSponsorId) {
      setProductSponsorId(storeData?.productSponsorId);
    }
  }, []);

  return (
    <section className="container-large py-5">
      {(isFetching || isFetching1 || isFetching2) && <Spinner />}
      <ErrorMessage error={error || error1 || error2} wrapperClassName="mb-2" />

      <Button
        id="addManufacturer"
        text={labels?.productManufacturer?.addManufacturer}
        wrapperClassName="col-md-3 mb-2"
        click={addNewManufacturer}
      />
      <AgGrid
        wrapperClassNames="mb-5 d-none d-md-block"
        printClassNames="mt-n3"
        columnDefs={columnDefsForPM}
        rowData={rowDataForPM}
        domLayout={"normal"}
        getRowClass={getRowClassPM}
        onCellClicked={onCellClicked}
        onFirstDataRendered={onFirstDataRendered}
      />
      <MobileResponsiveGrid
        key="productManufacturers"
        rowData={sortedEntitiesPM}
        columnDefs={columnDefsForPM}
        selectedRowId={productSponsorId}
        getRowClass={getRowClassPM}
        onRowClick={(e) => {
          setProductSponsorId(e.data.id);
        }}
        showSortDropDown={true}
        sortOption={sortOptionPM}
        setSortOption={setSortOptionPM}
        
      />

      <Heading
        text={labels?.productManufacturer?.contacts}
        wrapperClassNames="h4 border-bottom-1 pb-2 mb-3 mt-3"
        title={false}
      />

      <Button
        id="addContact"
        text={labels?.productManufacturer?.addContact}
        wrapperClassName="col-md-3 col-lg-2 mb-2"
        click={addNewContact}
      />

      <AgGrid
        wrapperClassNames="mb-3 d-none d-md-block"
        printClassNames="mt-n3"
        columnDefs={columnDefsContacts}
        rowData={rowDataContacts}
        domLayout={"normal"}
        overlayNoRowsTemplate={labels?.productManufacturer?.noContacts}
      />

      <MobileResponsiveGrid
        key="contacts"
        rowData={rowDataContacts}
        columnDefs={columnDefsContacts}
        noDataLabel={labels?.productManufacturer?.noContacts}
      />

      <InfoPopup
        showModal={showConfirmPopup}
        primaryButtonText={labels?.productManufacturer.ok}
        primaryButtonClick={removeContact}
        secondaryButtonText={labels?.productManufacturer.cancel}
        secondaryButtonClick={() => setShowConfirmPopup("")}
        body={labels?.productManufacturer.contactRemove}
      />
    </section>
  );
};

export default ProductManufacturers;
